import moment from "moment";
import Store from "../../Store";
import { getContactNameFromRoster, getDataFromRoster, getLocalUserId } from "../../Helpers/Chat/User";
import { getGroupData } from "../../Helpers/Chat/Group";
import { isGroupChat } from "../../Helpers/Chat/ChatHelper";

/**
 * Builds global search results out of the data already in the store, used as the offline
 * fallback when the live search request cannot be completed. Everything here carries the
 * user's real chat and message ids, so results stay navigable.
 *
 * Everything here is shaped exactly like the documented API response, so the components
 * and the response normalizer stay unchanged when the real endpoint replaces this.
 */

const MEDIA_LABELS = {
    image: "Photo",
    video: "Video",
    audio: "Audio",
    file: "Document",
    location: "Location",
    contact: "Contact",
    meet: "Meeting"
};

/** The API reports times as a UTC "YYYY-MM-DD HH:mm:ss" string; the store keeps epoch microseconds. */
const toSentTime = (timestamp) => {
    if (!timestamp) return "";
    return moment(Number(timestamp) / 1000).utc().format("YYYY-MM-DD HH:mm:ss");
};

const getMessagePreview = (msgBody = {}, msgType = "") => {
    const { message = "", media = {} } = msgBody;
    const messageType = msgType || msgBody.message_type || "";
    if (messageType === "text" || messageType === "auto_text") return message;
    return media?.caption || media?.fileName || MEDIA_LABELS[messageType] || "";
};

const getDisplayName = (userId = "") => {
    const roster = getDataFromRoster(userId);
    return (roster && getContactNameFromRoster(roster)) || userId;
};

const isRenderableMessage = (msg = {}) =>
    Boolean(msg?.msgId) && msg.deleteStatus === 0 && msg.recallStatus === 0;

/**
 * In the store a message's `fromUserId` is the conversation it belongs to and
 * `publisherId` is who actually sent it. The API instead reports a from/to pair,
 * so map the conversation onto whichever side the local user is not on.
 */
const toApiMessage = (msg, chatId) => {
    const { msgId, msgBody = {}, chatType = "", timestamp } = msg;
    const localUserId = getLocalUserId();
    const senderId = msg.publisherId || msg.fromUserId || "";
    const isGroup = isGroupChat(chatType);
    const sentByLocalUser = senderId === localUserId;

    const fromUser = isGroup || !sentByLocalUser ? senderId : localUserId;
    const toUser = isGroup ? chatId : sentByLocalUser ? chatId : localUserId;

    return {
        // Timestamps are stable and ordered, which is what the message cursor needs.
        id: Number(timestamp) || 0,
        messageId: msgId,
        message: getMessagePreview(msgBody),
        messageType: msgBody.message_type || "text",
        chatType: isGroup ? "groupchat" : "singlechat",
        fromUser,
        fromUserName: isGroup ? getDisplayName(senderId) : sentByLocalUser ? "" : getDisplayName(chatId),
        fromUserImage: "",
        fromUserThumbImage: "",
        toUser,
        toUserName: isGroup ? getGroupData(chatId)?.groupName || "" : getDisplayName(toUser),
        toUserImage: "",
        toUserThumbImage: "",
        sentTime: toSentTime(timestamp),
        deliveryStatus: 2,
        recallStatus: 0,
        starredStatus: msg.favouriteStatus || 0,
        deletedStatus: 0,
        epochTimestamp: Number(timestamp) || 0,
        jid: localUserId
    };
};

/** Every loaded message across every conversation, newest first. */
export const getStoreMessages = () => {
    const { chatConversationHistory: { data = {} } = {} } = Store.getState() || {};
    const messages = [];
    Object.keys(data).forEach((chatId) => {
        const chatMessages = data[chatId]?.messages || {};
        Object.values(chatMessages).forEach((msg) => {
            if (!isRenderableMessage(msg)) return;
            const apiMessage = toApiMessage(msg, chatId);
            if (apiMessage.message) messages.push(apiMessage);
        });
    });
    return messages.sort((a, b) => b.id - a.id);
};

export const getStoreRecentChats = () => {
    const { recentChatData: { data = [] } = {} } = Store.getState() || {};
    const localUserId = getLocalUserId();
    return [...data]
        .sort((a, b) => (b.messageTime || 0) - (a.messageTime || 0))
        .map((chat) => {
            const { fromUserId = "", chatType = "", msgBody = {}, msgId = "", msgType = "" } = chat;
            const isGroup = isGroupChat(chatType);
            const name = isGroup ? getGroupData(fromUserId)?.groupName || "" : getDisplayName(fromUserId);
            return {
                chatType: isGroup ? "groupchat" : "singlechat",
                messageType: msgType || msgBody.message_type || "text",
                messageId: msgId,
                message: getMessagePreview(msgBody, msgType),
                // Recent chats already carry a UTC time string, same as the API's sentTime.
                sentTime: chat.createdAt || toSentTime(chat.timestamp),
                deliveryStatus: chat.msgStatus ?? 2,
                archiveStatus: chat.archiveStatus || 0,
                fromUser: fromUserId,
                fromUserName: name,
                fromUserImage: "",
                fromUserThumbImage: "",
                toUser: localUserId,
                toUserName: "",
                participant: chat.publisherId || fromUserId,
                participantName: name,
                recallStatus: chat.recallStatus || 0,
                unread: chat.unreadCount || 0,
                epochTimestamp: Number(chat.timestamp) || 0,
                messageTime: chat.messageTime || 0,
                muteStatus: chat.muteStatus || 0,
                jid: localUserId
            };
        });
};

export const getStoreContacts = () => {
    const { rosterData: { data = [] } = {} } = Store.getState() || {};
    const localUserId = getLocalUserId();
    return data
        .filter((roster) => roster?.userId && roster.userId !== localUserId)
        .map((roster) => ({
            username: roster.userId,
            nickname: getContactNameFromRoster(roster) || roster.userId,
            image: roster.image || "",
            thumbImage: roster.thumbImage || ""
        }));
};

export const getStoreGroups = () => {
    const { groupsData: { data = [] } = {} } = Store.getState() || {};
    return data
        .filter((group) => group?.groupId)
        .map((group) => ({
            groupId: group.groupId,
            groupName: group.groupName || "",
            image: group.groupImage || "",
            thumbImage: group.thumbImage || ""
        }));
};

export const getStoreCollections = () => ({
    contacts: getStoreContacts(),
    groups: getStoreGroups(),
    recentChats: getStoreRecentChats(),
    messages: getStoreMessages()
});
